import { KeyValue } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ODataFilter } from '@shared/modules/grid/abstractions/odata-filter-column';
import { DataType } from '@shared/helpers/enums/data-type';
import { FilterTypes } from '@shared/modules/prime-grid-filters/helpers/enums/filter-type.enum';
import { of } from 'rxjs';
import { transactionTypeItems } from '../../admin/constants/transaction-type-items';
import { DropdownItem } from '@shared/abstraction';
import { GridColumn } from '@grid/abstractions';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import {
  Collection,
  EntityType,
  InvestmentJurisdiction,
  SecurityType,
} from 'src/app/modules/admin/models';
import { EntityTypeService } from '@core/services/entity-type/entity-type.service';
import { SecurityTypeService } from '@core/services/security-type/security-type.service';
import { asyncFilterOptionsFn } from '@shared/modules/prime-grid-filters/helpers/functions/async-filter-option';
import { InvestmentJurisdictionService } from '../../admin/services';
import { ITEM_ROOT } from 'odata-query';
import { CurrentUserService } from '@shared/services';
import { UserService } from '@shared/services/user.service';
import { User } from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class AuditHistoryHeadersService {
  private readonly translateService = inject(TranslateService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly userService = inject(UserService);
  private readonly entityTypeService = inject(EntityTypeService);
  private readonly securityTypeService = inject(SecurityTypeService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );

  getHeaders(): GridColumn[] {
    return [
      {
        label: this.translateService.instant('securityAudit.client'),
        key: 'clientName',
        sortKey: 'security.entity.client.name',
        dataType: DataType.String,
        isSortable: true,
      },
      {
        label: this.translateService.instant('securityAudit.transactionType'),
        key: 'transactionTypeName',
        sortKey: 'security.transactionType.transactionTypeId',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'security/transactionType/transactionTypeId',
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              eq: columnFilter.getValue<KeyValue<string, string>[]>()[0].value,
            });
          },
          options: {
            items: of(
              transactionTypeItems
                .filter(
                  (item: DropdownItem) => item.value !== TransactionType.All
                )
                .map((item: DropdownItem) => {
                  return { value: item.value, key: item.label } as KeyValue<
                    string,
                    string
                  >;
                })
            ),
          },
        },
      },
      {
        label: this.translateService.instant('securityAudit.entityType'),
        key: 'entityTypeName',
        sortKey: 'security.entity.entityType.entityTypeId',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'security/entity/entityType/name',
          options: {
            isMultiple: true,
            items: asyncFilterOptionsFn(
              this.entityTypeService.get(),
              (entityTypeCollection: Collection<EntityType>) => {
                return entityTypeCollection.items.map(
                  (entityType: EntityType) => {
                    return {
                      value: entityType.entityTypeId
                        ? entityType.entityTypeId.toString()
                        : '',
                      key: entityType.name ? entityType.name.toString() : '',
                    } as KeyValue<string, string>;
                  }
                );
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant('securityAudit.securityType'),
        key: 'securityTypeName',
        sortKey: 'security.securityType.securityTypeId',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'security/securityType/name',
          options: {
            isMultiple: true,
            items: asyncFilterOptionsFn(
              this.securityTypeService.activeSecurityTypeList(),
              (securityTypeCollection: Collection<SecurityType>) => {
                return securityTypeCollection.items.map(
                  (securityType: SecurityType) => {
                    return {
                      value: securityType.securityTypeId
                        ? securityType.securityTypeId.toString()
                        : '',
                      key: securityType.name
                        ? securityType.name.toString()
                        : '',
                    } as KeyValue<string, string>;
                  }
                );
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant(
          'securityAudit.investmentJurisdiction'
        ),
        key: 'investmentJurisdictionName',
        sortKey: 'security.investmentJurisdiction.investmentJurisdictionId',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Combobox,
          oDataPath: 'security/investmentJurisdiction/country/name',
          options: {
            isMultiple: true,
            allowSearch: true,
            items: asyncFilterOptionsFn(
              this.investmentJurisdictionService.countryList,
              ({ items }: Collection<InvestmentJurisdiction>) => {
                return items.map(
                  (investmentJurisdiction: InvestmentJurisdiction) => {
                    return {
                      value: investmentJurisdiction.investmentJurisdictionId
                        ? investmentJurisdiction.investmentJurisdictionId.toString()
                        : '',
                      key: investmentJurisdiction.name
                        ? investmentJurisdiction.name.toString()
                        : '',
                    } as KeyValue<string, string>;
                  }
                );
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant('securityAudit.modified'),
        key: 'modified',
        sortKey: 'modified',
        dataType: DataType.Date,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Datepicker,
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              ge: columnFilter.getValue<string[]>()[0],
              le: columnFilter.getValue<string[]>()[1],
            });
          },
        },
      },
      {
        label: this.translateService.instant('securityAudit.description'),
        key: 'description',
        sortKey: 'description',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.TextField,
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              contains: columnFilter.getValue<string>(),
            });
          },
        },
      },
      {
        label: this.translateService.instant('securityAudit.user'),
        key: 'userName',
        sortKey: 'createdBy',
        isVisible: this.currentUserService.isAdmin(),
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Combobox,
          oDataPath: 'createdBy',
          isVisible: (): boolean => {
            return this.currentUserService.isAdmin();
          },
          options: {
            isMultiple: true,
            allowSearch: true,
            items: asyncFilterOptionsFn(
              this.userService.getAllUser({
                filter: { isActive: true },
                orderBy: 'firstName',
              }),
              (usersCollection: Collection<User>) => {
                return usersCollection.items.map((user: User) => {
                  return {
                    value: user.id ? user.id.toString() : '',
                    key: user.fullName ? user.fullName.toString() : '',
                  } as KeyValue<string, string>;
                });
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            const fullNames = columnFilter
              .getValue<KeyValue<string, string>[]>()
              .map(item => {
                return { [ITEM_ROOT]: { contains: item.key.trim() } };
              });

            columnFilter.updateQuery({
              and: [{ or: fullNames }],
            });
          },
        },
      },
    ];
  }
}
